import React, { useContext } from 'react';
import { ApiContext } from 'src/context/apiContext';
import { useParams } from 'react-router-dom';
import { ContentLayout } from 'src/components/ContentLayout';

import { OperatorFlowVisualizationContainer } from 'src/components/OperatorFlowVisualizationContainer';
import { useQuery } from '@tanstack/react-query';
import { apiGetRequest } from 'src/utils/mlPigeonAPIRequest';
import { OperatorModalProvider } from 'src/context/operatorModalContext';
import {
  Container,
  KeyValuePairs,
  SpaceBetween,
} from '@cloudscape-design/components';
import { VariableListCell } from 'src/components/VariableListCell';
import { OperatorFlowLink } from 'src/components/OperatorFlowLink';

export function OperatorFlowPage() {
  const { state } = useContext(ApiContext);

  const { applicationId, modelVersionId, modelArtifactId, operatorFlowId } =
    useParams<Record<string, string>>();

  const operatorFlowDataQuery = useQuery<OperatorFlowJob, Error>({
    queryKey: ['operator-flow-data', operatorFlowId],
    queryFn: () =>
      apiGetRequest({
        endpoint: `operatorFlowJobs/${operatorFlowId}`,
      }),
  });

  const applicationDetails = state.applications.data.find(
    (app) => app['id'] === applicationId,
  );

  const modelVersionDetails = state.modelVersions.data.find(
    (mv) => mv['id'] === modelVersionId,
  );

  const modelArtifactDetails = state.modelArtifacts.data.find(
    (ma) => ma['id'] === modelArtifactId,
  );

  return (
    <>
      <ContentLayout
        heading={
          <>
            <div className="operator-flow-details__header">
              <div className="operator-flow-details__heading-container">
                <h1>Operator Flow</h1>
                {operatorFlowDataQuery.data?.rolledBackOperatorFlowJobId && (
                  <div className="badge">Redeploy</div>
                )}
              </div>
              <div className="operator-flow-details__sub-heading">
                {operatorFlowId}
              </div>
            </div>
          </>
        }
      >
        <div className="operator-flow-visualization">
          <SpaceBetween size="m">
            <Container>
              <KeyValuePairs
                columns={3}
                items={[
                  {
                    label: 'Application Name',
                    value: applicationDetails?.name || '-',
                  },
                  {
                    label: 'Model Version',
                    value: modelVersionDetails?.name || '-',
                  },
                  {
                    label: 'Model Artifact',
                    value: modelArtifactDetails?.modelArtifactName || '-',
                  },
                  {
                    label: 'Region',
                    value: operatorFlowDataQuery?.data?.region || '-',
                  },
                  {
                    label: 'Origin Operator Flow Job',
                    value:
                      operatorFlowDataQuery?.data
                        ?.rolledBackOperatorFlowJobId || '-',
                  },
                  {
                    label: 'Redeployment Operator Flow Jobs',
                    value: operatorFlowDataQuery?.data
                      ?.redeployedOperatorFlowJobIds
                      ? operatorFlowDataQuery?.data?.redeployedOperatorFlowJobIds.map(
                          (id) => (
                            <div key={id}>
                              <OperatorFlowLink operatorFlowJobId={id} />
                            </div>
                          ),
                        )
                      : '-',
                  },
                  {
                    label: 'Variables',
                    value: operatorFlowDataQuery?.data?.variables ? (
                      <VariableListCell
                        variables={operatorFlowDataQuery?.data?.variables}
                      />
                    ) : (
                      '-'
                    ),
                  },
                ]}
              />
            </Container>
            <div>
              <OperatorModalProvider>
                <OperatorFlowVisualizationContainer
                  operatorFlowDetails={operatorFlowDataQuery.data}
                  operatorFlowDataLoading={operatorFlowDataQuery.isLoading}
                />
              </OperatorModalProvider>
              {operatorFlowDataQuery.error && (
                <p className="error-text">
                  {operatorFlowDataQuery.error.message}
                </p>
              )}
            </div>
          </SpaceBetween>
        </div>
      </ContentLayout>
    </>
  );
}
